import { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { AbortedError } from '@/lib/concurrency'
import { useReportCache } from '@/stores/reportCache'
import {
  fetchAddressHourlyHistory,
  getAddressCapabilities,
  hasMeteredEnergy,
  hourlyHistoryRequestCount,
  hourlyNetPowerW,
  type HourlySlot,
} from '@/lib/energyHistory'
import {
  fetchHourlyPrices,
  priceRequestCount,
  priceWindowStart,
} from '@/lib/electricityPrices'
import { recommendInverterKw } from '@/lib/batterySizing'

export type ReportStatus = 'idle' | 'running' | 'done' | 'error' | 'cancelled'

export interface BatteryReportData {
  slots: HourlySlot[]
  /** Recommended inverter power from the 95th percentile of hourly net grid power. */
  inverterKw: number
  /** Hourly EUR/kWh (incl. BTW) as entries, keyed by UTC hour ISO. */
  priceEntries: Array<[string, number]>
  periodDays: number
  hasSmartMeter: boolean
  hasSolar: boolean
  fromIso: string
  toIso: string
}

/**
 * Data phase of the battery advice report: fetches a period of hourly energy
 * history (also the basis for inverter sizing) and hourly spot prices.
 * The simulation itself is pure and runs reactively on top of this data, so
 * tweaking strategy or rates does not refetch anything.
 */
export function useBatteryReport(addressUuid: string | null, months: number) {
  const cacheKey = addressUuid ? `batteryAdvice:${addressUuid}:${months}` : null
  const setEntry = useReportCache((s) => s.setEntry)
  const readCache = (key: string | null) =>
    key ? useReportCache.getState().getEntry<BatteryReportData>(key) : undefined

  const [status, setStatus] = useState<ReportStatus>(() => (readCache(cacheKey) ? 'done' : 'idle'))
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const [data, setData] = useState<BatteryReportData | null>(() => readCache(cacheKey)?.data ?? null)
  const [generatedAt, setGeneratedAt] = useState<string | null>(
    () => readCache(cacheKey)?.generatedAt ?? null
  )
  const [error, setError] = useState<unknown>(null)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    const cached = readCache(cacheKey)
    setData(cached?.data ?? null)
    setGeneratedAt(cached?.generatedAt ?? null)
    setStatus(cached ? 'done' : 'idle')
    setProgress({ done: 0, total: 0 })
    setError(null)
  }, [cacheKey])

  useEffect(() => () => abortRef.current?.abort(), [])

  const cancel = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  const run = useCallback(async () => {
    if (!addressUuid || !cacheKey) return
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    const { signal } = controller

    setStatus('running')
    setError(null)
    setData(null)

    // Whole months ending at local midnight today (matches companion-web).
    const to = new Date()
    to.setHours(0, 0, 0, 0)
    const from = new Date(to)
    from.setMonth(from.getMonth() - months)
    const pricesFrom = priceWindowStart(from, to)

    const total =
      1 + // devices
      hourlyHistoryRequestCount() +
      priceRequestCount()
    let done = 0
    setProgress({ done, total })
    const bump = () => {
      done++
      setProgress({ done, total })
    }

    try {
      const capabilities = await getAddressCapabilities(addressUuid, signal)
      bump()

      const [slots, priceMap] = await Promise.all([
        fetchAddressHourlyHistory(addressUuid, from, to, { signal, onRequestDone: bump }),
        fetchHourlyPrices(pricesFrom, to, { signal, onRequestDone: bump }),
      ])

      if (signal.aborted) {
        setStatus('cancelled')
        return
      }

      const periodDays = Math.max(1, Math.round((to.getTime() - from.getTime()) / 86_400_000))
      const result: BatteryReportData = {
        slots,
        inverterKw: recommendInverterKw(hourlyNetPowerW(slots)),
        priceEntries: [...priceMap.entries()],
        periodDays,
        hasSmartMeter: capabilities.hasSmartMeter || hasMeteredEnergy(slots),
        hasSolar: capabilities.hasSolar,
        fromIso: from.toISOString(),
        toIso: to.toISOString(),
      }
      const stamp = new Date().toISOString()
      setData(result)
      setGeneratedAt(stamp)
      setStatus('done')
      setEntry<BatteryReportData>(cacheKey, result, stamp)
    } catch (err) {
      if (err instanceof AbortedError || axios.isCancel(err) || signal.aborted) {
        setStatus('cancelled')
      } else {
        setError(err)
        setStatus('error')
      }
    }
  }, [addressUuid, cacheKey, months, setEntry])

  return { status, progress, data, generatedAt, error, run, cancel }
}
